/**
 * PedidosPage Component
 * 
 * Página de criação e gerenciamento de pedidos.
 * Interface similar a PDV com busca de produtos, carrinho e finalização.
 * 
 * Funcionalidades:
 * - Busca de produtos
 * - Adição de produtos ao pedido
 * - Seleção de vendedor e tabela de preços
 * - Finalização de pré-venda, orçamento ou consignado
 * - Modal obrigatório de abertura de caixa
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, Plus, Minus, User, FileText, XCircle } from 'lucide-react'
import CashRegisterModal from '../../../../components/ERP/CashRegisterModal/CashRegisterModal'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { 
  cashRegisterService, 
  saleService, 
  productService
} from '../../../../services/erpApi'
import './PedidosPage.css'

function PedidosPage() {
  const navigate = useNavigate()
  
  // Estados do caixa
  const [cashRegisterModalOpen, setCashRegisterModalOpen] = useState(false)
  const [cashRegisterStatus, setCashRegisterStatus] = useState(null)
  const [checkingCashRegister, setCheckingCashRegister] = useState(true)
  const [closeCashRegisterModalOpen, setCloseCashRegisterModalOpen] = useState(false)
  const [actualAmount, setActualAmount] = useState('')
  const [closingCashRegister, setClosingCashRegister] = useState(false)

  // Estados do pedido
  const [searchTerm, setSearchTerm] = useState('')
  const [products, setProducts] = useState([])
  const [selectedProducts, setSelectedProducts] = useState([])
  const [quantity, setQuantity] = useState('1,000')
  const [unitPrice, setUnitPrice] = useState('0,00')
  const [selectedSeller, setSelectedSeller] = useState('Vendedor')
  const [selectedPriceTable, setSelectedPriceTable] = useState('Tab. de preços')
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [observations, setObservations] = useState('')
  const [loading, setLoading] = useState(false)

  // Adicionar produto ao pedido
  const handleAddProduct = useCallback((product) => {
    const qty = parseFloat(quantity.replace(',', '.')) || 1
    const price = parseFloat(unitPrice.replace(',', '.')) || product.price || 0
    
    setSelectedProducts(prev => {
      const existingIndex = prev.findIndex(p => p.id === product.id)
      
      if (existingIndex >= 0) {
        // Atualizar quantidade
        const updated = [...prev]
        updated[existingIndex].quantity += qty
        updated[existingIndex].totalPrice = updated[existingIndex].quantity * price
        return updated
      } else {
        // Adicionar novo produto
        return [
          ...prev,
          {
            ...product,
            quantity: qty,
            unitPrice: price,
            totalPrice: qty * price
          }
        ]
      }
    })

    // Limpar campos
    setSearchTerm('')
    setQuantity('1,000')
    setUnitPrice('0,00')
    setProducts([])
  }, [quantity, unitPrice])

  // Verificar status do caixa ao carregar
  useEffect(() => {
    checkCashRegisterStatus()
  }, [])

  // Busca automática quando o usuário digita 2 ou mais caracteres
  useEffect(() => {
    // Limpar resultados se o campo estiver vazio
    if (!searchTerm.trim()) {
      setProducts([])
      return
    }

    // Se tiver menos de 2 caracteres, não buscar ainda
    if (searchTerm.trim().length < 2) {
      setProducts([])
      return
    }

    // Debounce: aguardar 300ms após o usuário parar de digitar
    const timeoutId = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await productService.getAll({ 
          search: searchTerm,
          active: true,
          inStock: true
        })
        
        if (response.success) {
          setProducts(response.data || [])
          
          // Se for código de barras (apenas números) e tiver resultado único, 
          // apenas mostrar no dropdown - não adicionar automaticamente
          // O usuário pode clicar para adicionar e ajustar a quantidade antes
        } else {
          setProducts([])
        }
      } catch (error) {
        console.error('Erro ao buscar produtos:', error)
        setProducts([])
      } finally {
        setLoading(false)
      }
    }, 300)

    // Limpar timeout se o usuário continuar digitando
    return () => clearTimeout(timeoutId)
  }, [searchTerm, handleAddProduct])

  const checkCashRegisterStatus = async () => {
    try {
      setCheckingCashRegister(true)
      const response = await cashRegisterService.checkOpenStatus()
      
      if (response.success) {
        setCashRegisterStatus(response.data)
        
        // Se não houver caixa aberto, abrir modal obrigatório
        if (!response.data.isOpen) {
          setCashRegisterModalOpen(true)
        }
      }
    } catch (error) {
      console.error('Erro ao verificar status do caixa:', error)
    } finally {
      setCheckingCashRegister(false)
    }
  }

  const handleCashRegisterOpened = (cashRegister) => {
    setCashRegisterStatus({
      isOpen: true,
      cashRegister
    })
    setCashRegisterModalOpen(false)
  }

  const handleExitFromModal = () => {
    navigate('/erp/dashboard')
  }

  // Fechar caixa
  const handleCloseCashRegister = async () => {
    if (!actualAmount || parseFloat(actualAmount.replace(',', '.')) <= 0) {
      alert('Por favor, informe o valor atual do caixa')
      return
    }

    if (!cashRegisterStatus?.cashRegister?.id) {
      alert('Erro: Caixa não encontrado')
      return
    }

    setClosingCashRegister(true)
    try {
      const amount = parseFloat(actualAmount.replace(',', '.'))
      const response = await cashRegisterService.close(
        cashRegisterStatus.cashRegister.id,
        amount
      )

      if (response.success) {
        alert('Caixa fechado com sucesso!')
        setCloseCashRegisterModalOpen(false)
        setActualAmount('')
        // Atualizar status do caixa
        await checkCashRegisterStatus()
      } else {
        alert(response.message || 'Erro ao fechar caixa')
      }
    } catch (error) {
      console.error('Erro ao fechar caixa:', error)
      alert(error.response?.data?.message || 'Erro ao fechar caixa')
    } finally {
      setClosingCashRegister(false)
    }
  }

  // Remover produto do pedido
  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId))
  }

  // Atualizar quantidade de produto
  const handleUpdateQuantity = (productId, newQuantity) => {
    setSelectedProducts(selectedProducts.map(p => {
      if (p.id === productId) {
        return {
          ...p,
          quantity: newQuantity,
          totalPrice: newQuantity * p.unitPrice
        }
      }
      return p
    }))
  }

  // Calcular totais
  const calculateTotals = () => {
    const totalItems = selectedProducts.length
    const totalAmount = selectedProducts.reduce((sum, p) => sum + p.totalPrice, 0)
    const totalDiscount = 0 // TODO: Implementar desconto
    const totalSurcharge = 0 // TODO: Implementar acréscimo
    const finalAmount = totalAmount - totalDiscount + totalSurcharge

    return {
      totalItems,
      totalAmount,
      totalDiscount,
      totalSurcharge,
      finalAmount
    }
  }

  const totals = calculateTotals()

  // Formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Finalizar pré-venda
  const handleFinalizePresale = async () => {
    if (selectedProducts.length === 0) {
      alert('Adicione pelo menos um produto ao pedido')
      return
    }

    setLoading(true)
    try {
      const saleData = {
        type: 'presale',
        customerId: selectedCustomer?.id || null,
        saleDate: new Date().toISOString(),
        totalAmount: totals.totalAmount,
        discountAmount: totals.totalDiscount,
        taxAmount: totals.totalSurcharge,
        finalAmount: totals.finalAmount,
        status: 'draft',
        observation: observations
      }

      const response = await saleService.create(saleData)
      
      if (response.success) {
        // Adicionar itens
        for (const product of selectedProducts) {
          await saleService.addItem(response.data.id, {
            productId: product.id,
            quantity: product.quantity,
            unitPrice: product.unitPrice,
            totalPrice: product.totalPrice
          })
        }

        alert('Pré-venda criada com sucesso!')
        // Limpar formulário
        setSelectedProducts([])
        setObservations('')
        setSelectedCustomer(null)
      }
    } catch (error) {
      console.error('Erro ao finalizar pré-venda:', error)
      alert('Erro ao finalizar pré-venda. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  // Se ainda está verificando o caixa, mostrar loading
  if (checkingCashRegister) {
    return (
      <div className="pedidos-page loading">
        <div className="loading-spinner">Carregando...</div>
      </div>
    )
  }

  return (
    <div className="pedidos-page">
      {/* Modal de Abertura de Caixa */}
      <CashRegisterModal
        isOpen={cashRegisterModalOpen}
        onClose={handleExitFromModal}
        onOpen={handleCashRegisterOpened}
        required={true}
      />

      {/* Modal de Fechamento de Caixa */}
      <Modal
        isOpen={closeCashRegisterModalOpen}
        onClose={() => {
          setCloseCashRegisterModalOpen(false)
          setActualAmount('')
        }}
        title="Fechar Caixa"
        size="small"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setCloseCashRegisterModalOpen(false)
                setActualAmount('')
              }}
              disabled={closingCashRegister}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleCloseCashRegister}
              disabled={closingCashRegister}
            >
              {closingCashRegister ? 'Fechando...' : 'Fechar Caixa'}
            </button>
          </>
        }
      >
        <div style={{ padding: '1rem 0' }}>
          <FormField
            label="Valor Atual do Caixa"
            name="actualAmount"
            type="text"
            value={actualAmount}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, '')
              if (value) {
                value = (parseInt(value) / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
              }
              setActualAmount(value)
            }}
            placeholder="0,00"
            required
          />
          {cashRegisterStatus?.cashRegister && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', background: '#f3f4f6', borderRadius: '8px' }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                <strong>Valor de Abertura:</strong> {formatCurrency(cashRegisterStatus.cashRegister.opening_amount || 0)}
              </p>
            </div>
          )}
        </div>
      </Modal>

      <div className="pedidos-container">
        {/* Header com botão de fechar caixa */}
        {cashRegisterStatus?.isOpen && cashRegisterStatus?.cashRegister && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '1rem 2rem',
            background: 'white',
            borderRadius: '8px',
            marginBottom: '1rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>
                <strong>Caixa Aberto</strong>
                {cashRegisterStatus.cashRegister.opened_by_name && (
                  <> — Operador: {cashRegisterStatus.cashRegister.opened_by_name}</>
                )}
                {' '}- Valor de Abertura: {formatCurrency(cashRegisterStatus.cashRegister.opening_amount || 0)}
              </p>
            </div>
            <button
              onClick={() => setCloseCashRegisterModalOpen(true)}
              style={{
                padding: '0.5rem 1rem',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}
            >
              <XCircle size={18} />
              Fechar Caixa
            </button>
          </div>
        )}

        {/* Top Section - Busca e Seleções */}
        <div className="pedidos-top-section">
          <div className="search-container">
            <div className="search-input-wrapper">
              <Search className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Pesquise por código, cód. barras, produto ou referência (mín. 2 caracteres)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  // Se pressionar Enter e tiver resultado único, adicionar automaticamente
                  if (e.key === 'Enter' && products.length === 1) {
                    e.preventDefault()
                    handleAddProduct(products[0])
                  }
                }}
                disabled={loading}
                autoFocus
              />
              {loading && (
                <span style={{ 
                  position: 'absolute', 
                  right: '1rem', 
                  color: '#667eea',
                  fontSize: '0.875rem'
                }}>
                  Buscando...
                </span>
              )}
              
              {/* Dropdown de resultados */}
              {products.length > 0 && (
                <div className="search-results-dropdown">
                  <div className="search-results-header">
                    {products.length} produto(s) encontrado(s) com estoque disponível
                  </div>
                  <div className="search-results-list">
                    {products.map(product => (
                      <div
                        key={product.id}
                        className="search-result-item"
                        onClick={() => handleAddProduct(product)}
                      >
                        <div className="result-info">
                          <strong>{product.name}</strong>
                          <span>SKU: {product.sku} | Estoque: {product.stock_quantity || 0}</span>
                        </div>
                        <div className="result-price">
                          {formatCurrency(product.price)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {searchTerm && !loading && products.length === 0 && (
                <div className="search-results-dropdown search-results-empty">
                  <div className="search-results-header">
                    Nenhum produto encontrado
                  </div>
                  <div style={{ padding: '1rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                    Nenhum produto com estoque disponível para "{searchTerm}"
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button type="button" className="advanced-search-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit', textDecoration: 'underline' }}>
                Pesq. avançada (F4)
              </button>
            </div>
          </div>

          <div className="selectors-container">
            <div className="selector-wrapper">
              <label>Escolha o vendedor</label>
              <select 
                className="selector-input"
                value={selectedSeller}
                onChange={(e) => setSelectedSeller(e.target.value)}
              >
                <option>Vendedor</option>
              </select>
            </div>
            <div className="selector-wrapper">
              <label>Tab. de preços</label>
              <select 
                className="selector-input"
                value={selectedPriceTable}
                onChange={(e) => setSelectedPriceTable(e.target.value)}
              >
                <option>Tab. de preços</option>
              </select>
            </div>
          </div>
        </div>

        {/* Middle Section - Inputs e Lista */}
        <div className="pedidos-middle-section">
          <div className="product-inputs">
            <div className="input-group">
              <label className="input-label">Quantidade</label>
              <input
                type="text"
                className="product-input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="1,000"
              />
            </div>
            <div className="input-group">
              <label className="input-label">Preço Unitário</label>
              <input
                type="text"
                className="product-input"
                value={unitPrice}
                onChange={(e) => setUnitPrice(e.target.value)}
                placeholder="R$0,00"
              />
            </div>
          </div>

          <div className="products-list">
            <div className="products-list-header">
              <div className="header-col">#</div>
              <div className="header-col">Produto</div>
              <div className="header-col">Total</div>
            </div>
            <div className="products-list-body">
              {selectedProducts.length === 0 ? (
                <div className="empty-state">
                  Não há nenhum produto escolhido.
                </div>
              ) : (
                selectedProducts.map((product, index) => (
                  <div key={product.id} className="product-item">
                    <div className="product-col">{index + 1}</div>
                    <div className="product-col product-name">
                      {product.name}
                      <div className="product-actions">
                        <button
                          className="action-btn"
                          onClick={() => handleUpdateQuantity(product.id, product.quantity - 1)}
                        >
                          <Minus size={16} />
                        </button>
                        <span className="quantity-display">{product.quantity}</span>
                        <button
                          className="action-btn"
                          onClick={() => handleUpdateQuantity(product.id, product.quantity + 1)}
                        >
                          <Plus size={16} />
                        </button>
                        <button
                          className="action-btn remove"
                          onClick={() => handleRemoveProduct(product.id)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="product-col">{formatCurrency(product.totalPrice)}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>


        {/* Bottom Section - Botões e Resumo */}
        <div className="pedidos-bottom-section">
          <div className="action-buttons-primary">
            <button
              className="btn-primary-action"
              onClick={handleFinalizePresale}
              disabled={loading || selectedProducts.length === 0}
            >
              Finalizar Pré-venda (F7)
            </button>
            <button className="btn-primary-action" disabled>
              Finalizar Orçamento (F8)
            </button>
            <button className="btn-primary-action" disabled>
              Finalizar Consignado (F9)
            </button>
          </div>

          <div className="action-buttons-secondary">
            <button className="btn-secondary-action">Recentes</button>
            <button className="btn-secondary-action">
              <User size={16} />
              Cliente (F2)
            </button>
            <button className="btn-secondary-action">
              <FileText size={16} />
              Observações (F12)
            </button>
          </div>

          <div className="order-summary">
            <div className="summary-item">
              <span>Pedido: Livre</span>
            </div>
            <div className="summary-item">
              <span>Total de itens: {totals.totalItems}</span>
            </div>
            <div className="summary-item">
              <span>Itens cancelados: 0</span>
            </div>
            <div className="summary-item">
              <span>Total de desconto: {formatCurrency(totals.totalDiscount)}</span>
            </div>
            <div className="summary-item">
              <span>Total de acréscimo: {formatCurrency(totals.totalSurcharge)}</span>
            </div>
          </div>

          <div className="finalization-buttons">
            <button className="btn-finalize" disabled>
              Finalizar detalhado (F5)
            </button>
            <button className="btn-cancel" onClick={() => navigate('/erp/dashboard')}>
              Cancelar (F6)
            </button>
          </div>

          <div className="grand-total">
            {formatCurrency(totals.finalAmount)}
          </div>
        </div>
      </div>
    </div>
  )
}

export default PedidosPage

